import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useCreateClaim } from '@/hooks/useWarranties';

const CLAIM_TYPES = [
  { value: 'failure', label: 'Equipment Failure' },
  { value: 'underperformance', label: 'Underperformance' },
  { value: 'capacity', label: 'Capacity Degradation' },
  { value: 'labor', label: 'Labor / Workmanship' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warrantyId: string;
  propertyId: string;
  componentType: string;
  providerName: string;
}

export function FileClaimDialog({ open, onOpenChange, warrantyId, propertyId, componentType, providerName }: Props) {
  const createClaim = useCreateClaim();
  const [claimType, setClaimType] = useState('failure');
  const [description, setDescription] = useState('');

  const handleSubmit = () => {
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    createClaim.mutate(
      {
        warranty_id: warrantyId,
        property_id: propertyId,
        claim_type: claimType,
        description: description.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Claim filed as draft');
          setClaimType('failure');
          setDescription('');
          onOpenChange(false);
        },
        onError: (err) => toast.error(`Failed to file claim: ${err.message}`),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>File Warranty Claim</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {componentType} warranty from {providerName}
          </p>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="claimType">Claim Type</Label>
            <Select value={claimType} onValueChange={setClaimType}>
              <SelectTrigger id="claimType"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLAIM_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              placeholder="Describe the issue, symptoms, and timeline..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createClaim.isPending}>
            File Claim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
